import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { EVENTS } from '@apps/web/lib/events';
import { useMode } from '@apps/web/lib/mode_store';
import { ApiError, type components } from '@libraries/typescript/api_client';

type PhoebeAgentRunEventRead = components['schemas']['PhoebeAgentRunEventRead'];
type PhoebeAgentRunPendingEventRead =
    components['schemas']['PhoebeAgentRunPendingEventRead'];
export type PhoebeAgentRunHeaderRead =
    components['schemas']['PhoebeAgentRunHeaderRead'];
type PhoebeAgentRunResourceRead =
    components['schemas']['PhoebeAgentRunResourceRead'];
export type PhoebeAgentRunInvestigationTaskStateRead =
    components['schemas']['PhoebeAgentRunInvestigationTaskStateRead'];
export type PhoebeAgentRunStateRead =
    components['schemas']['PhoebeAgentRunStateRead'];

export type PhoebeAgentRunEventStreamCursor = {
    readonly after_offset: number;
    readonly after_pending_created_at: string | null;
    readonly after_pending_id: string | null;
    readonly after_pending_status_updated_at: string | null;
    readonly after_pending_status_id: string | null;
};

type SnapshotLine = {
    readonly type: 'snapshot';
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pending_events: Array<PhoebeAgentRunPendingEventRead>;
    readonly pending_event_updates: Array<PhoebeAgentRunPendingEventRead>;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

type EventLine = {
    readonly type: 'event';
    readonly event: PhoebeAgentRunEventRead;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

type PendingEventLine = {
    readonly type: 'pending_event';
    readonly pending_event: PhoebeAgentRunPendingEventRead;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

type PendingEventUpdateLine = {
    readonly type: 'pending_event_update';
    readonly pending_event_update: PhoebeAgentRunPendingEventRead;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

type RunStateLine = {
    readonly type: 'run_state';
    readonly run_state: PhoebeAgentRunStateRead;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

type HeartbeatLine = {
    readonly type: 'heartbeat';
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

export type PhoebeAgentRunEventStreamDoneReason =
    | 'page_full'
    | 'cycle_complete'
    | 'idle_timeout';

type DoneLine = {
    readonly type: 'done';
    readonly reason: PhoebeAgentRunEventStreamDoneReason;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
};

export type PhoebeAgentRunEventStreamLine =
    | SnapshotLine
    | EventLine
    | PendingEventLine
    | PendingEventUpdateLine
    | RunStateLine
    | HeartbeatLine
    | DoneLine;

export type PhoebeAgentRunEventStreamConnectionStatus =
    | 'idle'
    | 'connecting'
    | 'open'
    | 'waiting'
    | 'reconnecting'
    | 'error'
    | 'not_found';

type InternalState = {
    readonly eventsById: Map<string, PhoebeAgentRunEventRead>;
    readonly eventIds: Array<string>;
    readonly eventIdByOffset: Map<number, string>;
    readonly pendingEventsById: Map<string, PhoebeAgentRunPendingEventRead>;
    readonly pendingEventIds: Array<string>;
    readonly suppressedPendingEventIds: Set<string>;
    readonly liveEventIds: Set<string>;
    readonly liveEventAnimationDeadlinesById: Map<string, number>;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
    readonly runState: PhoebeAgentRunStateRead | null;
    readonly investigationTaskStateRevision: number;
    readonly connectionStatus: PhoebeAgentRunEventStreamConnectionStatus;
    readonly error: Error | null;
    readonly snapshotApplied: boolean;
    readonly pendingEventRetention: PendingEventRetention;
};

export type PendingEventRetention = 'pending-only' | 'include-rejected';

export type PhoebeAgentRunEventStreamSnapshot = {
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pendingEvents: Array<PhoebeAgentRunPendingEventRead>;
    readonly liveEventIds: ReadonlySet<string>;
    readonly runState: PhoebeAgentRunStateRead | null;
    readonly investigationTaskStateRevision: number;
    readonly cursor: PhoebeAgentRunEventStreamCursor;
    readonly connectionStatus: PhoebeAgentRunEventStreamConnectionStatus;
    readonly error: Error | null;
    readonly isLoading: boolean;
    readonly isBusy: boolean;
    readonly isIdle: boolean;
    readonly isGenerating: boolean;
    readonly isWaitingForApproval: boolean;
    readonly isWaitingForUserInput: boolean;
    readonly refresh: () => void;
};

type ApplyLineAction = {
    readonly type: 'apply_line';
    readonly line: PhoebeAgentRunEventStreamLine;
};

type SetConnectionStatusAction = {
    readonly type: 'set_connection_status';
    readonly connectionStatus: PhoebeAgentRunEventStreamConnectionStatus;
    readonly error?: Error | null;
};

type ResetAction = {
    readonly type: 'reset';
    readonly pendingEventRetention?: PendingEventRetention;
};

type ReducerAction = ApplyLineAction | SetConnectionStatusAction | ResetAction;

const INITIAL_CURSOR: PhoebeAgentRunEventStreamCursor = {
    after_offset: -1,
    after_pending_created_at: null,
    after_pending_id: null,
    after_pending_status_updated_at: null,
    after_pending_status_id: null,
};
const EMPTY_RUN_HEADER: PhoebeAgentRunHeaderRead = {
    initial_user_message_event_id: null,
    initial_user_message_contents: null,
    latest_message_event_id: null,
    latest_message_contents: null,
    latest_message_event_at: null,
    latest_status_event_id: null,
    latest_status_contents: null,
    latest_status_event_at: null,
    latest_loop_event_id: null,
    latest_loop_contents: null,
    latest_loop_event_at: null,
    latest_approval_event_id: null,
    latest_approval_contents: null,
    latest_approval_event_at: null,
};

const STREAM_REASON = 'phoebe-agent-run-events-stream';
const STREAM_WAIT_MS = 25_000;
// Delay between long-poll cycles when the run is no longer producing events.
// Keeps idle reconnects calm without flooding the server.
const STREAM_IDLE_RECONNECT_DELAY_MS = 4_000;
const STREAM_IDLE_RECONNECT_JITTER_RATIO = 0.25;
// Delay between long-poll cycles while the run is actively producing events.
// Each long-poll closes as soon as the server flushes a batch, so this is the
// gap between batches reaching the UI — keep it small to avoid visible lag.
const STREAM_ACTIVE_RECONNECT_DELAY_MS = 100;
const STREAM_RETRY_DELAYS_MS = [500, 1_000, 2_000, 5_000] as const;
// 404s are deterministic for a given stream identity (run id + mode):
// retrying cannot succeed until an input changes, and any input change
// restarts the loop. A small allowance absorbs read-after-write races on
// freshly created runs.
const STREAM_NOT_FOUND_TERMINAL_THRESHOLD = 3;
const LIVE_EVENT_TYPEWRITER_MAX_AGE_MS = 3_000;

function normalizeCursorPair(
    timestamp: string | null,
    id: string | null,
): readonly [string | null, string | null] {
    if (timestamp && id) {
        return [timestamp, id];
    }
    return [null, null];
}

export function normalizePhoebeAgentRunEventStreamCursorForRequest(
    cursor: PhoebeAgentRunEventStreamCursor,
): PhoebeAgentRunEventStreamCursor {
    const [afterPendingCreatedAt, afterPendingId] = normalizeCursorPair(
        cursor.after_pending_created_at,
        cursor.after_pending_id,
    );
    const [afterPendingStatusUpdatedAt, afterPendingStatusId] =
        normalizeCursorPair(
            cursor.after_pending_status_updated_at,
            cursor.after_pending_status_id,
        );

    return {
        ...cursor,
        after_pending_created_at: afterPendingCreatedAt,
        after_pending_id: afterPendingId,
        after_pending_status_updated_at: afterPendingStatusUpdatedAt,
        after_pending_status_id: afterPendingStatusId,
    };
}

function createInitialState(
    pendingEventRetention: PendingEventRetention = 'pending-only',
): InternalState {
    return {
        eventsById: new Map(),
        eventIds: [],
        eventIdByOffset: new Map(),
        pendingEventsById: new Map(),
        pendingEventIds: [],
        suppressedPendingEventIds: new Set(),
        liveEventIds: new Set(),
        liveEventAnimationDeadlinesById: new Map(),
        cursor: INITIAL_CURSOR,
        runState: null,
        investigationTaskStateRevision: 0,
        connectionStatus: 'idle',
        error: null,
        snapshotApplied: false,
        pendingEventRetention,
    };
}

function cloneState(state: InternalState): InternalState {
    return {
        ...state,
        eventsById: new Map(state.eventsById),
        eventIds: [...state.eventIds],
        eventIdByOffset: new Map(state.eventIdByOffset),
        pendingEventsById: new Map(state.pendingEventsById),
        pendingEventIds: [...state.pendingEventIds],
        suppressedPendingEventIds: new Set(state.suppressedPendingEventIds),
        liveEventIds: new Set(state.liveEventIds),
        liveEventAnimationDeadlinesById: new Map(
            state.liveEventAnimationDeadlinesById,
        ),
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCursor(value: unknown): value is PhoebeAgentRunEventStreamCursor {
    return (
        isRecord(value) &&
        typeof value['after_offset'] === 'number' &&
        (typeof value['after_pending_created_at'] === 'string' ||
            value['after_pending_created_at'] === null) &&
        (typeof value['after_pending_id'] === 'string' ||
            value['after_pending_id'] === null) &&
        (typeof value['after_pending_status_updated_at'] === 'string' ||
            value['after_pending_status_updated_at'] === null) &&
        (typeof value['after_pending_status_id'] === 'string' ||
            value['after_pending_status_id'] === null)
    );
}

function isEvent(value: unknown): value is PhoebeAgentRunEventRead {
    return (
        isRecord(value) &&
        typeof value['id'] === 'string' &&
        typeof value['event_offset'] === 'number' &&
        typeof value['type'] === 'string' &&
        isRecord(value['payload']) &&
        typeof value['created_at'] === 'string'
    );
}

function isPendingEvent(
    value: unknown,
): value is PhoebeAgentRunPendingEventRead {
    return (
        isRecord(value) &&
        typeof value['id'] === 'string' &&
        typeof value['type'] === 'string' &&
        isRecord(value['payload']) &&
        (value['status'] === 'pending' ||
            value['status'] === 'drained' ||
            value['status'] === 'rejected') &&
        typeof value['interrupts'] === 'boolean' &&
        typeof value['created_at'] === 'string'
    );
}

function readNullableString(
    value: Record<string, unknown>,
    key: string,
): string | null | undefined {
    const fieldValue = value[key];
    if (fieldValue === undefined || fieldValue === null) {
        return null;
    }
    if (typeof fieldValue === 'string') {
        return fieldValue;
    }
    return undefined;
}

function readNullableRecord(
    value: Record<string, unknown>,
    key: string,
): Record<string, unknown> | null | undefined {
    const fieldValue = value[key];
    if (fieldValue === undefined || fieldValue === null) {
        return null;
    }
    if (isRecord(fieldValue)) {
        return fieldValue;
    }
    return undefined;
}

function parseRunHeader(value: unknown): PhoebeAgentRunHeaderRead | null {
    if (value === undefined) {
        return EMPTY_RUN_HEADER;
    }
    if (!isRecord(value)) {
        return null;
    }

    const header = {
        initial_user_message_event_id: readNullableString(
            value,
            'initial_user_message_event_id',
        ),
        initial_user_message_contents: readNullableRecord(
            value,
            'initial_user_message_contents',
        ),
        latest_message_event_id: readNullableString(
            value,
            'latest_message_event_id',
        ),
        latest_message_contents: readNullableRecord(
            value,
            'latest_message_contents',
        ),
        latest_message_event_at: readNullableString(
            value,
            'latest_message_event_at',
        ),
        latest_status_event_id: readNullableString(
            value,
            'latest_status_event_id',
        ),
        latest_status_contents: readNullableRecord(
            value,
            'latest_status_contents',
        ),
        latest_status_event_at: readNullableString(
            value,
            'latest_status_event_at',
        ),
        latest_loop_event_id: readNullableString(value, 'latest_loop_event_id'),
        latest_loop_contents: readNullableRecord(value, 'latest_loop_contents'),
        latest_loop_event_at: readNullableString(value, 'latest_loop_event_at'),
        latest_approval_event_id: readNullableString(
            value,
            'latest_approval_event_id',
        ),
        latest_approval_contents: readNullableRecord(
            value,
            'latest_approval_contents',
        ),
        latest_approval_event_at: readNullableString(
            value,
            'latest_approval_event_at',
        ),
    };

    if (Object.values(header).some((fieldValue) => fieldValue === undefined)) {
        return null;
    }
    return header as PhoebeAgentRunHeaderRead;
}

function parseRunResources(
    value: unknown,
): Array<PhoebeAgentRunResourceRead> | null {
    if (value === undefined) {
        return [];
    }
    if (!Array.isArray(value)) {
        return null;
    }

    const resources: Array<PhoebeAgentRunResourceRead> = [];
    for (const item of value) {
        if (
            !isRecord(item) ||
            typeof item['id'] !== 'string' ||
            !(
                item['resource_type'] === 'outreach' ||
                item['resource_type'] === 'shift' ||
                item['resource_type'] === 'caregiver' ||
                item['resource_type'] === 'client' ||
                item['resource_type'] === 'coordinator_automation' ||
                item['resource_type'] === 'agent_automation'
            ) ||
            typeof item['resource_id'] !== 'string' ||
            !(
                item['role'] === 'primary' ||
                item['role'] === 'related' ||
                item['role'] === 'runner'
            ) ||
            !(
                typeof item['created_by_event_id'] === 'string' ||
                item['created_by_event_id'] === null
            ) ||
            typeof item['created_at'] !== 'string'
        ) {
            return null;
        }

        resources.push({
            id: item['id'],
            resource_type: item['resource_type'],
            resource_id: item['resource_id'],
            role: item['role'],
            created_by_event_id: item['created_by_event_id'],
            created_at: item['created_at'],
        });
    }

    return resources;
}

function parseInvestigationTaskState(
    value: unknown,
): PhoebeAgentRunInvestigationTaskStateRead | null | undefined {
    if (value === undefined || value === null) {
        return null;
    }
    if (
        !isRecord(value) ||
        typeof value['execution_count'] !== 'number' ||
        typeof value['task_count'] !== 'number' ||
        !(
            typeof value['latest_execution_updated_at'] === 'string' ||
            value['latest_execution_updated_at'] === null
        ) ||
        !(
            typeof value['latest_task_updated_at'] === 'string' ||
            value['latest_task_updated_at'] === null
        )
    ) {
        return undefined;
    }
    return {
        execution_count: value['execution_count'],
        task_count: value['task_count'],
        latest_execution_updated_at: value['latest_execution_updated_at'],
        latest_task_updated_at: value['latest_task_updated_at'],
    };
}

function parseRunState(value: unknown): PhoebeAgentRunStateRead | null {
    if (
        !isRecord(value) ||
        typeof value['id'] !== 'string' ||
        !(
            value['status'] === 'idle' ||
            value['status'] === 'running' ||
            value['status'] === 'failed' ||
            value['status'] === 'completed' ||
            value['status'] === 'cancelled'
        ) ||
        !(
            value['activity_type'] === 'chat' ||
            value['activity_type'] === 'workflow' ||
            value['activity_type'] === undefined
        ) ||
        !(
            value['agent_runtime'] === 'v2' ||
            value['agent_runtime'] === 'v3' ||
            value['agent_runtime'] === 'orchestrator' ||
            value['agent_runtime'] === 'caregiver_sms' ||
            value['agent_runtime'] === 'admin' ||
            value['agent_runtime'] === undefined
        ) ||
        !(
            typeof value['is_subagent'] === 'boolean' ||
            value['is_subagent'] === undefined
        ) ||
        !(
            value['purpose'] === 'outreach' ||
            value['purpose'] === 'agent_automation' ||
            value['purpose'] === 'coordinator_automation' ||
            value['purpose'] === null ||
            value['purpose'] === undefined
        ) ||
        !(
            typeof value['is_workflow'] === 'boolean' ||
            value['is_workflow'] === undefined
        ) ||
        typeof value['has_pending_work'] !== 'boolean' ||
        typeof value['interrupt_requested'] !== 'boolean' ||
        typeof value['has_pending_approval'] !== 'boolean' ||
        typeof value['has_pending_user_input'] !== 'boolean' ||
        typeof value['has_active_lease'] !== 'boolean' ||
        !(
            typeof value['is_generating'] === 'boolean' ||
            value['is_generating'] === undefined
        ) ||
        !(
            typeof value['last_processed_at'] === 'string' ||
            value['last_processed_at'] === null
        ) ||
        typeof value['updated_at'] !== 'string'
    ) {
        return null;
    }
    const header = parseRunHeader(value['header']);
    if (!header) {
        return null;
    }
    const resources = parseRunResources(value['resources']);
    if (!resources) {
        return null;
    }
    const adminInvestigationTaskState = parseInvestigationTaskState(
        value['admin_investigation_task_state'],
    );
    if (adminInvestigationTaskState === undefined) {
        return null;
    }

    const activityType = value['activity_type'] ?? 'chat';
    // Pre-D3 API tasks omit these during rollout; M8 removes this tolerance.
    return {
        id: value['id'],
        status: value['status'],
        activity_type: activityType,
        agent_runtime: value['agent_runtime'] ?? 'v2',
        is_subagent: value['is_subagent'] ?? false,
        purpose: value['purpose'] ?? null,
        is_workflow: value['is_workflow'] ?? activityType === 'workflow',
        has_pending_work: value['has_pending_work'],
        interrupt_requested: value['interrupt_requested'],
        has_pending_approval: value['has_pending_approval'],
        has_pending_user_input: value['has_pending_user_input'],
        has_active_lease: value['has_active_lease'],
        is_generating: value['is_generating'] ?? false,
        last_processed_at: value['last_processed_at'],
        updated_at: value['updated_at'],
        header,
        resources,
        watches: parseRunWatches(value['watches']),
        admin_investigation_task_state: adminInvestigationTaskState,
    };
}

function parseRunWatches(value: unknown): PhoebeAgentRunStateRead['watches'] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(
        (
            watch,
        ): watch is NonNullable<PhoebeAgentRunStateRead['watches']>[number] =>
            isRecord(watch) &&
            typeof watch['id'] === 'string' &&
            typeof watch['reason'] === 'string' &&
            typeof watch['status'] === 'string' &&
            typeof watch['expires_at'] === 'string',
    );
}

function investigationTaskStateKey(
    state: PhoebeAgentRunInvestigationTaskStateRead | null | undefined,
): string {
    if (!state) {
        return 'none';
    }
    return [
        state.execution_count,
        state.task_count,
        state.latest_execution_updated_at ?? '',
        state.latest_task_updated_at ?? '',
    ].join('|');
}

function hasInvestigationTaskRows(
    state: PhoebeAgentRunInvestigationTaskStateRead | null | undefined,
): boolean {
    return (
        state !== null &&
        state !== undefined &&
        (state.execution_count > 0 || state.task_count > 0)
    );
}

function isDoneReason(
    value: unknown,
): value is PhoebeAgentRunEventStreamDoneReason {
    return (
        value === 'page_full' ||
        value === 'cycle_complete' ||
        value === 'idle_timeout'
    );
}

function getCursorFromLine(
    line: PhoebeAgentRunEventStreamLine,
): PhoebeAgentRunEventStreamCursor {
    return line.cursor;
}

export function parsePhoebeAgentRunEventStreamLine(
    rawLine: string,
): PhoebeAgentRunEventStreamLine {
    const parsed: unknown = JSON.parse(rawLine);
    if (!isRecord(parsed) || typeof parsed['type'] !== 'string') {
        throw new Error('Invalid Phoebe agent run event stream line');
    }

    const cursor = parsed['cursor'];
    if (!isCursor(cursor)) {
        throw new Error('Invalid Phoebe agent run event stream cursor');
    }

    switch (parsed['type']) {
        case 'snapshot': {
            const events = parsed['events'];
            const pendingEvents = parsed['pending_events'];
            const pendingEventUpdates = parsed['pending_event_updates'];
            if (
                !Array.isArray(events) ||
                !events.every(isEvent) ||
                !Array.isArray(pendingEvents) ||
                !pendingEvents.every(isPendingEvent) ||
                !Array.isArray(pendingEventUpdates) ||
                !pendingEventUpdates.every(isPendingEvent)
            ) {
                throw new Error('Invalid Phoebe agent run snapshot line');
            }
            return {
                type: 'snapshot',
                events,
                pending_events: pendingEvents,
                pending_event_updates: pendingEventUpdates,
                cursor,
            };
        }
        case 'event': {
            if (!isEvent(parsed['event'])) {
                throw new Error('Invalid Phoebe agent run event line');
            }
            return { type: 'event', event: parsed['event'], cursor };
        }
        case 'pending_event': {
            if (!isPendingEvent(parsed['pending_event'])) {
                throw new Error('Invalid Phoebe agent run pending event line');
            }
            return {
                type: 'pending_event',
                pending_event: parsed['pending_event'],
                cursor,
            };
        }
        case 'pending_event_update': {
            if (!isPendingEvent(parsed['pending_event_update'])) {
                throw new Error(
                    'Invalid Phoebe agent run pending event update line',
                );
            }
            return {
                type: 'pending_event_update',
                pending_event_update: parsed['pending_event_update'],
                cursor,
            };
        }
        case 'run_state': {
            const runState = parseRunState(parsed['run_state']);
            if (!runState) {
                throw new Error('Invalid Phoebe agent run state line');
            }
            return {
                type: 'run_state',
                run_state: runState,
                cursor,
            };
        }
        case 'heartbeat':
            return { type: 'heartbeat', cursor };
        case 'done': {
            if (!isDoneReason(parsed['reason'])) {
                throw new Error('Invalid Phoebe agent run done line');
            }
            return { type: 'done', reason: parsed['reason'], cursor };
        }
        default:
            throw new Error('Unknown Phoebe agent run event stream line type');
    }
}

export function parsePhoebeAgentRunEventStreamChunk({
    bufferedText,
    chunkText,
}: {
    readonly bufferedText: string;
    readonly chunkText: string;
}): {
    readonly bufferedText: string;
    readonly lines: Array<PhoebeAgentRunEventStreamLine>;
} {
    const parts = `${bufferedText}${chunkText}`.split('\n');
    const nextBufferedText = parts.pop() ?? '';
    const lines = parts
        .filter((line) => line.trim().length > 0)
        .map((line) => parsePhoebeAgentRunEventStreamLine(line));

    return {
        bufferedText: nextBufferedText,
        lines,
    };
}

function sortEventIds(
    eventsById: Map<string, PhoebeAgentRunEventRead>,
): Array<string> {
    return [...eventsById.values()]
        .sort((left, right) => {
            const offsetCompare = left.event_offset - right.event_offset;
            return offsetCompare !== 0
                ? offsetCompare
                : left.id.localeCompare(right.id);
        })
        .map((event) => event.id);
}

function sortPendingEventIds(
    pendingEventsById: Map<string, PhoebeAgentRunPendingEventRead>,
): Array<string> {
    return [...pendingEventsById.values()]
        .sort((left, right) => {
            const createdAtCompare = left.created_at.localeCompare(
                right.created_at,
            );
            return createdAtCompare !== 0
                ? createdAtCompare
                : left.id.localeCompare(right.id);
        })
        .map((pendingEvent) => pendingEvent.id);
}

function removePendingEvent(
    state: InternalState,
    pendingEventId: string,
): InternalState {
    const pendingEventsById = new Map(state.pendingEventsById);
    pendingEventsById.delete(pendingEventId);
    return {
        ...state,
        pendingEventsById,
        pendingEventIds: state.pendingEventIds.filter(
            (id) => id !== pendingEventId,
        ),
    };
}

function upsertEvent(
    state: InternalState,
    event: PhoebeAgentRunEventRead,
): InternalState {
    let nextState = cloneState(state);
    const previousEvent = nextState.eventsById.get(event.id);
    if (previousEvent) {
        nextState.eventIdByOffset.delete(previousEvent.event_offset);
    }

    const existingIdAtOffset = nextState.eventIdByOffset.get(
        event.event_offset,
    );
    if (existingIdAtOffset && existingIdAtOffset !== event.id) {
        nextState.eventsById.delete(existingIdAtOffset);
    }

    nextState.eventsById.set(event.id, event);
    nextState.eventIdByOffset.set(event.event_offset, event.id);
    nextState = {
        ...nextState,
        eventIds: sortEventIds(nextState.eventsById),
    };

    if (
        event.source_pending_event_id &&
        nextState.pendingEventsById.has(event.source_pending_event_id)
    ) {
        nextState = removePendingEvent(
            nextState,
            event.source_pending_event_id,
        );
        nextState.suppressedPendingEventIds.add(event.source_pending_event_id);
    }

    return nextState;
}

function getLiveEventAnimationDeadlineMs(
    event: PhoebeAgentRunEventRead,
): number | null {
    const createdAtMs = Date.parse(event.created_at);
    if (!Number.isFinite(createdAtMs)) {
        return null;
    }

    const deadlineMs = createdAtMs + LIVE_EVENT_TYPEWRITER_MAX_AGE_MS;
    return Date.now() <= deadlineMs ? deadlineMs : null;
}

function pruneExpiredLiveEventIds(state: InternalState): InternalState {
    if (state.liveEventIds.size === 0) {
        return state;
    }

    const now = Date.now();
    let changed = false;
    const liveEventIds = new Set<string>();
    const liveEventAnimationDeadlinesById = new Map<string, number>();
    for (const [eventId, deadlineMs] of state.liveEventAnimationDeadlinesById) {
        if (deadlineMs > now) {
            liveEventIds.add(eventId);
            liveEventAnimationDeadlinesById.set(eventId, deadlineMs);
            continue;
        }
        changed = true;
    }

    for (const eventId of state.liveEventIds) {
        if (!liveEventAnimationDeadlinesById.has(eventId)) {
            changed = true;
        }
    }

    return changed
        ? {
              ...state,
              liveEventIds,
              liveEventAnimationDeadlinesById,
          }
        : state;
}

function upsertPendingEvent(
    state: InternalState,
    pendingEvent: PhoebeAgentRunPendingEventRead,
): InternalState {
    const nextState = cloneState(state);
    if (nextState.suppressedPendingEventIds.has(pendingEvent.id)) {
        return nextState;
    }

    const shouldRetainPendingEvent =
        pendingEvent.status === 'pending' ||
        (pendingEvent.status === 'rejected' &&
            nextState.pendingEventRetention === 'include-rejected');
    if (!shouldRetainPendingEvent) {
        if (nextState.pendingEventsById.has(pendingEvent.id)) {
            const removedState = removePendingEvent(nextState, pendingEvent.id);
            removedState.suppressedPendingEventIds.add(pendingEvent.id);
            return removedState;
        }
        return nextState;
    }

    nextState.pendingEventsById.set(pendingEvent.id, pendingEvent);
    return {
        ...nextState,
        pendingEventIds: sortPendingEventIds(nextState.pendingEventsById),
    };
}

function applyPendingEventUpdate(
    state: InternalState,
    pendingEvent: PhoebeAgentRunPendingEventRead,
): InternalState {
    let nextState = cloneState(state);
    const existingPendingEvent = nextState.pendingEventsById.get(
        pendingEvent.id,
    );

    const shouldRetainPendingEvent =
        pendingEvent.status === 'pending' ||
        (pendingEvent.status === 'rejected' &&
            nextState.pendingEventRetention === 'include-rejected');
    if (shouldRetainPendingEvent) {
        if (
            (existingPendingEvent ||
                (pendingEvent.status === 'rejected' &&
                    nextState.pendingEventRetention === 'include-rejected')) &&
            !nextState.suppressedPendingEventIds.has(pendingEvent.id)
        ) {
            const pendingEventsById = new Map(nextState.pendingEventsById);
            pendingEventsById.set(pendingEvent.id, pendingEvent);
            return {
                ...nextState,
                pendingEventsById,
                pendingEventIds: sortPendingEventIds(pendingEventsById),
            };
        }
        return nextState;
    }

    if (existingPendingEvent) {
        nextState = removePendingEvent(nextState, pendingEvent.id);
        nextState.suppressedPendingEventIds.add(pendingEvent.id);
    }
    return nextState;
}

function applyCursor(
    state: InternalState,
    cursor: PhoebeAgentRunEventStreamCursor,
): InternalState {
    return {
        ...state,
        cursor,
    };
}

function applySnapshot(
    state: InternalState,
    line: SnapshotLine,
): InternalState {
    const startingState = state.snapshotApplied
        ? state
        : createInitialState(state.pendingEventRetention);
    let nextState: InternalState = {
        ...startingState,
        connectionStatus: 'open',
        error: null,
        snapshotApplied: true,
    };

    for (const event of line.events) {
        nextState = upsertEvent(nextState, event);
    }
    for (const pendingEvent of line.pending_events) {
        nextState = upsertPendingEvent(nextState, pendingEvent);
    }
    for (const pendingEventUpdate of line.pending_event_updates) {
        nextState = applyPendingEventUpdate(nextState, pendingEventUpdate);
    }

    return applyCursor(nextState, line.cursor);
}

function applyLine(
    state: InternalState,
    line: PhoebeAgentRunEventStreamLine,
): InternalState {
    const prunedState = pruneExpiredLiveEventIds(state);
    switch (line.type) {
        case 'snapshot':
            return applySnapshot(prunedState, line);
        case 'event': {
            const liveDeadlineMs = getLiveEventAnimationDeadlineMs(line.event);
            const nextState = upsertEvent(prunedState, line.event);
            if (liveDeadlineMs !== null) {
                nextState.liveEventIds.add(line.event.id);
                nextState.liveEventAnimationDeadlinesById.set(
                    line.event.id,
                    liveDeadlineMs,
                );
            }
            return applyCursor(
                {
                    ...nextState,
                    connectionStatus: 'open',
                    error: null,
                },
                line.cursor,
            );
        }
        case 'pending_event':
            return applyCursor(
                {
                    ...upsertPendingEvent(prunedState, line.pending_event),
                    connectionStatus: 'open',
                    error: null,
                },
                line.cursor,
            );
        case 'pending_event_update':
            return applyCursor(
                {
                    ...applyPendingEventUpdate(
                        prunedState,
                        line.pending_event_update,
                    ),
                    connectionStatus: 'open',
                    error: null,
                },
                line.cursor,
            );
        case 'run_state': {
            const previousInvestigationTaskState =
                prunedState.runState?.admin_investigation_task_state ?? null;
            const nextInvestigationTaskState =
                line.run_state.admin_investigation_task_state ?? null;
            const investigationTaskStateChanged =
                previousInvestigationTaskState !== null
                    ? investigationTaskStateKey(
                          previousInvestigationTaskState,
                      ) !==
                      investigationTaskStateKey(nextInvestigationTaskState)
                    : hasInvestigationTaskRows(nextInvestigationTaskState);
            const investigationTaskStateRevision = investigationTaskStateChanged
                ? prunedState.investigationTaskStateRevision + 1
                : prunedState.investigationTaskStateRevision;
            return applyCursor(
                {
                    ...prunedState,
                    runState: line.run_state,
                    investigationTaskStateRevision,
                    connectionStatus: 'open',
                    error: null,
                },
                line.cursor,
            );
        }
        case 'heartbeat':
            return applyCursor(
                {
                    ...prunedState,
                    connectionStatus: 'open',
                    error: null,
                },
                line.cursor,
            );
        case 'done':
            return applyCursor(
                {
                    ...prunedState,
                    connectionStatus:
                        line.reason === 'page_full' ? 'connecting' : 'waiting',
                    error: null,
                },
                line.cursor,
            );
        default:
            return state;
    }
}

export function phoebeAgentRunEventStreamReducer(
    state: InternalState,
    action: ReducerAction,
): InternalState {
    switch (action.type) {
        case 'apply_line':
            return applyLine(state, action.line);
        case 'set_connection_status':
            return {
                ...state,
                connectionStatus: action.connectionStatus,
                error: action.error ?? null,
            };
        case 'reset':
            return createInitialState(
                action.pendingEventRetention ?? state.pendingEventRetention,
            );
        default:
            return state;
    }
}

export function createPhoebeAgentRunEventStreamTestState(
    pendingEventRetention: PendingEventRetention = 'pending-only',
): InternalState {
    return createInitialState(pendingEventRetention);
}

export function derivePhoebeAgentRunEventStreamBusyState(
    runState: PhoebeAgentRunStateRead | null,
): Pick<
    PhoebeAgentRunEventStreamSnapshot,
    'isBusy' | 'isIdle' | 'isWaitingForApproval' | 'isWaitingForUserInput'
> {
    const isWaitingForApproval =
        runState?.has_pending_approval === true &&
        runState.has_pending_work === false &&
        runState.has_active_lease === false;
    const isWaitingForUserInput =
        runState?.has_pending_user_input === true &&
        runState.has_pending_work === false &&
        runState.has_active_lease === false;
    const isActivelyRunning =
        runState?.status === 'running' &&
        (runState.has_active_lease === true ||
            runState.has_pending_work === true ||
            runState.is_generating === true ||
            runState.interrupt_requested === true);
    const isBusy =
        !isWaitingForApproval &&
        !isWaitingForUserInput &&
        (isActivelyRunning ||
            runState?.has_pending_work === true ||
            runState?.has_active_lease === true ||
            runState?.is_generating === true ||
            runState?.interrupt_requested === true);
    const isIdle =
        runState?.status === 'idle' &&
        !isBusy &&
        !isWaitingForApproval &&
        !isWaitingForUserInput;

    return {
        isBusy,
        isIdle,
        isWaitingForApproval,
        isWaitingForUserInput,
    };
}

export type PhoebeAgentRunEventStreamPollPolicy = 'active' | 'idle' | 'stopped';

// Terminal runs never produce more events, but settled or waiting runs can
// still re-activate from server-side events, inbound messages, or another
// viewer's action. Keep those waiting states on the calm idle cadence.
export function derivePhoebeAgentRunEventStreamPollPolicy(
    runState: PhoebeAgentRunStateRead | null,
): PhoebeAgentRunEventStreamPollPolicy {
    if (
        runState?.status === 'completed' ||
        runState?.status === 'failed' ||
        runState?.status === 'cancelled'
    ) {
        return 'stopped';
    }
    if (derivePhoebeAgentRunEventStreamBusyState(runState).isBusy) {
        return 'active';
    }
    return 'idle';
}

function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

function toError(error: unknown): Error {
    return error instanceof Error ? error : new Error('Stream request failed');
}

function getApiErrorStatus(error: unknown): number | null {
    return error instanceof ApiError ? (error.status ?? null) : null;
}

function waitForDelay(delayMs: number, signal: AbortSignal): Promise<void> {
    if (delayMs <= 0 || signal.aborted) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const timeoutId = window.setTimeout(resolve, delayMs);
        signal.addEventListener(
            'abort',
            () => {
                window.clearTimeout(timeoutId);
                resolve();
            },
            { once: true },
        );
    });
}

function getJitteredStreamIdleReconnectDelayMs(): number {
    const jitterMultiplier =
        1 -
        STREAM_IDLE_RECONNECT_JITTER_RATIO +
        Math.random() * STREAM_IDLE_RECONNECT_JITTER_RATIO * 2;
    return Math.round(STREAM_IDLE_RECONNECT_DELAY_MS * jitterMultiplier);
}

async function readStreamCycle({
    body,
    signal,
    onLine,
}: {
    readonly body: ReadableStream<Uint8Array<ArrayBufferLike>>;
    readonly signal: AbortSignal;
    readonly onLine: (line: PhoebeAgentRunEventStreamLine) => void;
}): Promise<PhoebeAgentRunEventStreamDoneReason | null> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let bufferedText = '';
    let doneReason: PhoebeAgentRunEventStreamDoneReason | null = null;

    try {
        while (!signal.aborted) {
            const result = await reader.read();
            if (result.done) {
                break;
            }

            const parsed = parsePhoebeAgentRunEventStreamChunk({
                bufferedText,
                chunkText: decoder.decode(result.value, { stream: true }),
            });
            bufferedText = parsed.bufferedText;
            for (const line of parsed.lines) {
                onLine(line);
                if (line.type === 'done') {
                    doneReason = line.reason;
                }
            }
        }

        const finalText = decoder.decode();
        if (finalText.length > 0) {
            const parsed = parsePhoebeAgentRunEventStreamChunk({
                bufferedText,
                chunkText: finalText,
            });
            bufferedText = parsed.bufferedText;
            for (const line of parsed.lines) {
                onLine(line);
                if (line.type === 'done') {
                    doneReason = line.reason;
                }
            }
        }

        if (bufferedText.trim().length > 0) {
            const line = parsePhoebeAgentRunEventStreamLine(bufferedText);
            onLine(line);
            if (line.type === 'done') {
                doneReason = line.reason;
            }
        }
    } finally {
        reader.releaseLock();
    }

    return doneReason;
}

export function usePhoebeAgentRunEventStream({
    organizationId,
    phoebeAgentRunId,
    enabled = true,
    mode,
    variant = 'organization',
    pendingEventRetention = 'pending-only',
    initialAfterOffset = -1,
    initialSnapshot = true,
    excludeDrainedPendingEvents = false,
}: {
    readonly organizationId: string | null;
    readonly phoebeAgentRunId: string | null;
    readonly enabled?: boolean;
    readonly mode?: 'live' | 'sandbox';
    // 'admin' long-polls the admin stream route for internal runs, which
    // resolves the run's mode server-side; organizationId is still required
    // so stream identity and telemetry stay consistent.
    readonly variant?: 'organization' | 'admin';
    readonly pendingEventRetention?: PendingEventRetention;
    readonly initialAfterOffset?: number;
    readonly initialSnapshot?: boolean;
    readonly excludeDrainedPendingEvents?: boolean;
}): PhoebeAgentRunEventStreamSnapshot {
    const posthog = usePostHog();
    const currentMode = useMode();
    const effectiveMode = mode ?? currentMode;
    const [refreshNonce, setRefreshNonce] = useReducer(
        (current: number) => current + 1,
        0,
    );
    const [state, dispatch] = useReducer(
        phoebeAgentRunEventStreamReducer,
        undefined,
        () => createInitialState(pendingEventRetention),
    );
    const generationRef = useRef(0);
    const streamIdentityRef = useRef<string | null>(null);
    const terminalStreamIdentityRef = useRef<string | null>(null);
    const visibilityPausedStreamIdentityRef = useRef<string | null>(null);
    const lastCapturedStreamErrorKeyRef = useRef<string | null>(null);
    const [isDocumentVisible, setIsDocumentVisible] = useState<boolean>(() =>
        typeof document === 'undefined'
            ? true
            : document.visibilityState === 'visible',
    );
    const lastEffectDocumentVisibleRef = useRef(isDocumentVisible);
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        function handleVisibilityChange() {
            setIsDocumentVisible(document.visibilityState === 'visible');
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    useEffect(() => {
        generationRef.current += 1;
        const generation = generationRef.current;
        const wasDocumentVisible = lastEffectDocumentVisibleRef.current;
        const becameHidden = wasDocumentVisible && !isDocumentVisible;
        const becameVisible = !wasDocumentVisible && isDocumentVisible;
        lastEffectDocumentVisibleRef.current = isDocumentVisible;

        if (!enabled || !organizationId || !phoebeAgentRunId) {
            streamIdentityRef.current = null;
            terminalStreamIdentityRef.current = null;
            visibilityPausedStreamIdentityRef.current = null;
            lastCapturedStreamErrorKeyRef.current = null;
            dispatch({ type: 'reset', pendingEventRetention });
            return;
        }

        const streamIdentity = `${variant}:${effectiveMode}:${organizationId}:${phoebeAgentRunId}:${pendingEventRetention}:${initialAfterOffset}:${initialSnapshot}:${excludeDrainedPendingEvents}`;
        const shouldResetStreamState =
            streamIdentityRef.current !== streamIdentity;
        streamIdentityRef.current = streamIdentity;
        if (shouldResetStreamState) {
            dispatch({ type: 'reset', pendingEventRetention });
        }

        // Pause polling while the tab is hidden to avoid background long-poll
        // load. A new hidden stream still reports loading until its first
        // snapshot arrives after refocus.
        if (!isDocumentVisible) {
            visibilityPausedStreamIdentityRef.current = becameHidden
                ? streamIdentity
                : null;
            if (shouldResetStreamState) {
                dispatch({
                    type: 'set_connection_status',
                    connectionStatus: 'connecting',
                });
            }
            return;
        }

        const resumedSameStreamFromHidden =
            becameVisible &&
            visibilityPausedStreamIdentityRef.current === streamIdentity;
        visibilityPausedStreamIdentityRef.current = null;
        // Only a visibility-driven restart should preserve a terminal stop.
        // Manual refreshes and run reselection must request a fresh snapshot.
        if (
            resumedSameStreamFromHidden &&
            terminalStreamIdentityRef.current === streamIdentity
        ) {
            return;
        }
        terminalStreamIdentityRef.current = null;

        const abortController = new AbortController();
        const signal = abortController.signal;
        const activeOrganizationId = organizationId;
        const activePhoebeAgentRunId = phoebeAgentRunId;

        async function runLoop() {
            let includeSnapshot = initialSnapshot;
            let cursor = {
                ...INITIAL_CURSOR,
                after_offset: initialAfterOffset,
            };
            let retryIndex = 0;
            let consecutiveNotFoundCount = 0;
            let latestRunState: PhoebeAgentRunStateRead | null = null;

            while (!signal.aborted && generationRef.current === generation) {
                try {
                    dispatch({
                        type: 'set_connection_status',
                        connectionStatus:
                            includeSnapshot || retryIndex === 0
                                ? 'connecting'
                                : 'reconnecting',
                    });
                    cursor =
                        normalizePhoebeAgentRunEventStreamCursorForRequest(
                            cursor,
                        );
                    const streamQueryParams = {
                        after_offset: cursor.after_offset,
                        after_pending_created_at:
                            cursor.after_pending_created_at,
                        after_pending_id: cursor.after_pending_id,
                        after_pending_status_updated_at:
                            cursor.after_pending_status_updated_at,
                        after_pending_status_id: cursor.after_pending_status_id,
                        include_snapshot: includeSnapshot,
                        ...(excludeDrainedPendingEvents
                            ? { exclude_drained_pending_events: true }
                            : {}),
                        wait_ms: STREAM_WAIT_MS,
                    };
                    const response =
                        variant === 'admin'
                            ? await getApiClient().raw<
                                  '/admin/agent/runs/{run_id}/events/stream',
                                  'get'
                              >({
                                  method: 'GET',
                                  url: '/admin/agent/runs/{run_id}/events/stream',
                                  pathParams: {
                                      run_id: activePhoebeAgentRunId,
                                  },
                                  queryParams: streamQueryParams,
                                  headers: {
                                      Accept: 'application/x-ndjson',
                                  },
                                  reason: STREAM_REASON,
                                  signal,
                              })
                            : await getApiClient().raw<
                                  '/organizations/{organization_id}/runs/{run_id}/events/stream',
                                  'get'
                              >({
                                  method: 'GET',
                                  url: '/organizations/{organization_id}/runs/{run_id}/events/stream',
                                  pathParams: {
                                      organization_id: activeOrganizationId,
                                      run_id: activePhoebeAgentRunId,
                                  },
                                  queryParams: streamQueryParams,
                                  headers: {
                                      Accept: 'application/x-ndjson',
                                  },
                                  mode: effectiveMode,
                                  reason: STREAM_REASON,
                                  signal,
                              });

                    if (!response.body) {
                        throw new Error('Stream response body is missing');
                    }

                    retryIndex = 0;
                    consecutiveNotFoundCount = 0;
                    lastCapturedStreamErrorKeyRef.current = null;
                    dispatch({
                        type: 'set_connection_status',
                        connectionStatus: 'open',
                    });
                    const doneReason = await readStreamCycle({
                        body: response.body,
                        signal,
                        onLine: (line) => {
                            if (
                                signal.aborted ||
                                generationRef.current !== generation
                            ) {
                                return;
                            }

                            dispatch({ type: 'apply_line', line });
                            cursor = getCursorFromLine(line);
                            if (line.type === 'run_state') {
                                latestRunState = line.run_state;
                            }
                        },
                    });
                    includeSnapshot = false;

                    if (
                        signal.aborted ||
                        generationRef.current !== generation
                    ) {
                        return;
                    }

                    const pollPolicy =
                        derivePhoebeAgentRunEventStreamPollPolicy(
                            latestRunState,
                        );
                    // page_full means the server has more queued: drain it
                    // before honoring a terminal stop. Waiting runs keep the
                    // calm idle cadence because they can re-activate elsewhere.
                    if (
                        doneReason !== 'page_full' &&
                        pollPolicy === 'stopped'
                    ) {
                        terminalStreamIdentityRef.current = streamIdentity;
                        return;
                    }
                    const reconnectDelayMs =
                        doneReason === 'page_full'
                            ? 0
                            : pollPolicy === 'active'
                              ? STREAM_ACTIVE_RECONNECT_DELAY_MS
                              : getJitteredStreamIdleReconnectDelayMs();
                    await waitForDelay(reconnectDelayMs, signal);
                } catch (error) {
                    if (
                        signal.aborted ||
                        generationRef.current !== generation ||
                        isAbortError(error)
                    ) {
                        return;
                    }

                    const streamError = toError(error);
                    const apiStatus = getApiErrorStatus(error);
                    consecutiveNotFoundCount =
                        apiStatus === 404 ? consecutiveNotFoundCount + 1 : 0;
                    const isTerminalNotFound =
                        consecutiveNotFoundCount >=
                        STREAM_NOT_FOUND_TERMINAL_THRESHOLD;
                    const streamErrorKey = [
                        streamIdentity,
                        apiStatus,
                        streamError.name,
                        streamError.message,
                    ].join(':');
                    // The terminal capture bypasses the dedupe key: it shares
                    // the key with the first 404, which would swallow it.
                    if (
                        isTerminalNotFound ||
                        lastCapturedStreamErrorKeyRef.current !== streamErrorKey
                    ) {
                        posthog?.capture(
                            EVENTS.PHOEBE_AGENT_RUN_EVENT_STREAM_FAILED,
                            {
                                organization_id: activeOrganizationId,
                                run_id: activePhoebeAgentRunId,
                                mode: effectiveMode,
                                api_status: apiStatus,
                                error_name: streamError.name,
                                error_message: streamError.message,
                                include_snapshot: includeSnapshot,
                                retry_index: retryIndex,
                                terminal: isTerminalNotFound,
                                consecutive_not_found: consecutiveNotFoundCount,
                            },
                        );
                        lastCapturedStreamErrorKeyRef.current = streamErrorKey;
                    }

                    if (isTerminalNotFound) {
                        dispatch({
                            type: 'set_connection_status',
                            connectionStatus: 'not_found',
                            error: streamError,
                        });
                        return;
                    }

                    const retryDelayMs =
                        STREAM_RETRY_DELAYS_MS[
                            Math.min(
                                retryIndex,
                                STREAM_RETRY_DELAYS_MS.length - 1,
                            )
                        ] ??
                        STREAM_RETRY_DELAYS_MS[
                            STREAM_RETRY_DELAYS_MS.length - 1
                        ] ??
                        5_000;
                    retryIndex += 1;
                    dispatch({
                        type: 'set_connection_status',
                        connectionStatus: 'error',
                        error: streamError,
                    });
                    await waitForDelay(retryDelayMs, signal);
                }
            }
        }

        void runLoop();

        return () => {
            abortController.abort();
        };
    }, [
        effectiveMode,
        enabled,
        isDocumentVisible,
        pendingEventRetention,
        initialAfterOffset,
        initialSnapshot,
        excludeDrainedPendingEvents,
        organizationId,
        phoebeAgentRunId,
        posthog,
        refreshNonce,
        variant,
    ]);

    const events = useMemo(
        () =>
            state.eventIds
                .map((id) => state.eventsById.get(id))
                .filter((event): event is PhoebeAgentRunEventRead =>
                    Boolean(event),
                ),
        [state.eventIds, state.eventsById],
    );
    const pendingEvents = useMemo(
        () =>
            state.pendingEventIds
                .map((id) => state.pendingEventsById.get(id))
                .filter(
                    (
                        pendingEvent,
                    ): pendingEvent is PhoebeAgentRunPendingEventRead =>
                        Boolean(pendingEvent),
                ),
        [state.pendingEventIds, state.pendingEventsById],
    );
    const liveEventIds = pruneExpiredLiveEventIds(state).liveEventIds;
    const busyState = derivePhoebeAgentRunEventStreamBusyState(state.runState);
    const isGenerating = state.runState?.is_generating === true;
    const isLoading =
        enabled &&
        Boolean(organizationId) &&
        Boolean(phoebeAgentRunId) &&
        initialSnapshot &&
        !state.snapshotApplied &&
        (state.connectionStatus === 'connecting' ||
            state.connectionStatus === 'open');

    return {
        events,
        pendingEvents,
        liveEventIds,
        runState: state.runState,
        investigationTaskStateRevision: state.investigationTaskStateRevision,
        cursor: state.cursor,
        connectionStatus: state.connectionStatus,
        error: state.error,
        isLoading,
        isGenerating,
        refresh: setRefreshNonce,
        ...busyState,
    };
}
